import { useState } from 'react'
import { useInvalidate } from '@refinedev/core'
import { useMutation } from '@tanstack/react-query'
import { App } from 'antd'
import { useTranslation } from 'react-i18next'

import {
  productCatalogUpdateProductDetailsMutation,
  productCatalogUpdateProductPricingMutation,
  productCatalogUpdateProductImagesMutation,
  productCatalogUpdateProductCategoriesMutation,
  type ProductDetailsRoot,
} from '@repo/sdk'

import { handleSdkError } from '../../providers/sdkErrorUtils'
import { buildImagesPayload } from './buildImagesPayload'
import type { ProductCreateFormValues } from './ProductCreateFormTypes'

function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  return a.every((val, i) => val === b[i])
}

interface UseProductEditSaveReturn {
  save: (values: ProductCreateFormValues, original: ProductDetailsRoot) => void
  isPending: boolean
}

export function useProductEditSave(
  productId: string,
  onSuccess: () => void,
): UseProductEditSaveReturn {
  const invalidate = useInvalidate()
  const { message } = App.useApp()
  const { t } = useTranslation()
  const [isPending, setIsPending] = useState(false)

  const detailsMut = useMutation({ ...productCatalogUpdateProductDetailsMutation() })
  const pricingMut = useMutation({ ...productCatalogUpdateProductPricingMutation() })
  const imagesMut = useMutation({ ...productCatalogUpdateProductImagesMutation() })
  const categoriesMut = useMutation({ ...productCatalogUpdateProductCategoriesMutation() })

  const save = (values: ProductCreateFormValues, original: ProductDetailsRoot) => {
    setIsPending(true)
    void executeSave(values, original)
  }

  const executeSave = async (values: ProductCreateFormValues, original: ProductDetailsRoot) => {
    try {
      const promises: Promise<unknown>[] = []
      const nameOrDescChanged =
        values.name !== original.name ||
        (values.description ?? null) !== (original.description ?? null)
      if (nameOrDescChanged) {
        promises.push(
          detailsMut.mutateAsync({
            path: { productId },
            body: { name: values.name, description: values.description ?? null },
          }),
        )
      }
      const pricingChanged =
        values.price !== original.price.amount ||
        values.quantity !== original.quantity ||
        values.shippingFee !== (original.shippingFee?.amount ?? 0)
      if (pricingChanged) {
        promises.push(
          pricingMut.mutateAsync({
            path: { productId },
            body: {
              price: { amount: values.price, currency: 'KRW' },
              quantity: values.quantity,
              // The stock this screen was opened with: a stock left unchanged here keeps the
              // sales made meanwhile, and a changed one fails with 409 if stock moved (#287).
              expectedQuantity: original.quantity,
              shippingFee: { amount: values.shippingFee, currency: 'KRW' },
            },
          }),
        )
      }
      const origCats = original.categories.map((c) => c.id).sort()
      const newCats = [...values.productCategoryIds].sort()
      if (!arraysEqual(origCats, newCats)) {
        promises.push(
          categoriesMut.mutateAsync({
            path: { productId },
            body: { categoryIds: values.productCategoryIds },
          }),
        )
      }
      if (!arraysEqual(original.images, values.images)) {
        const imagesPayload = await buildImagesPayload(values.images)
        promises.push(
          imagesMut.mutateAsync({ path: { productId }, body: { images: imagesPayload } }),
        )
      }
      if (promises.length === 0) return onSuccess()
      // Every part runs to the end even when one fails, and the screen reloads either way — the
      // parts that were saved must show as saved (#287: a failed part used to leave the others'
      // results invisible behind a single 'edit failed').
      const results = await Promise.allSettled(promises)
      const failed = results.find((r): r is PromiseRejectedResult => r.status === 'rejected')
      if (failed) {
        await invalidate({
          resource: 'product-list',
          invalidates: ['list', 'detail'],
          id: productId,
        })
        throw failed.reason
      }
      message.success(t('pages.products.messages.editSuccess'))
      // refine keys product queries as ['data','default','product-list', …]:
      // a raw ['product-list'] key never matched, so list and detail stayed stale.
      await invalidate({ resource: 'product-list', invalidates: ['list', 'detail'], id: productId })
      onSuccess()
    } catch (error_) {
      const { statusCode, message: serverMessage } = handleSdkError(error_)
      if (statusCode === 403) {
        message.error(t('pages.products.messages.broadcastModifyRestricted'))
      } else if (statusCode === 409) {
        // Server explains the conflict (stock changed since the screen opened, orders exist).
        message.error(serverMessage)
      } else {
        message.error(t('pages.products.messages.editFailed'))
      }
    } finally {
      setIsPending(false)
    }
  }

  return { save, isPending }
}
